//useLiveCharacters.ts — the avatar characters, kept live: every admin save is pushed over a socket

import { useEffect, useState } from 'react'
import { LIVE_PATH, LIVE_PING, LIVE_PONG, type AvatarCharacter } from '../../shared/avatarCharacter'
import { fetchAvatarCharacters } from './avatarApi'

//Keeps the socket from looking idle to proxies that drop quiet connections.
const PING_MS = 30_000
//Backoff for a dropped channel, capped so a mirror is back within a minute of the network.
const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000]

export interface LiveCharacters {
	//null until the first load, so "no characters yet" is not shown while still loading.
	readonly characters: readonly AvatarCharacter[] | null
	readonly error: string | null
}

export function useLiveCharacters(): LiveCharacters {
	const [characters, setCharacters] = useState<readonly AvatarCharacter[] | null>(null)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		let socket: WebSocket | null = null
		let ping: number | undefined
		let retry: number | undefined
		let attempt = 0
		let dropped = false
		let disposed = false

		//Unchanged JSON keeps the same array, so a save that changed nothing re-renders nothing.
		const accept = (list: AvatarCharacter[]) =>
			setCharacters((current) => (JSON.stringify(current) === JSON.stringify(list) ? current : list))

		//A plain fetch too, so the carousel loads even where a proxy blocks WebSockets.
		const refetch = () =>
			fetchAvatarCharacters()
				.then((list) => {
					if (disposed) return
					setError(null)
					accept(list)
				})
				.catch((cause: Error) => {
					if (!disposed) setError(`Could not load characters: ${cause.message}`)
				})

		const connect = () => {
			const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
			const opened = new WebSocket(`${scheme}://${window.location.host}${LIVE_PATH}`)
			socket = opened
			opened.onopen = () => {
				attempt = 0
				//Saves made while this mirror was offline never reached it; catch up once.
				if (dropped) void refetch()
				ping = window.setInterval(() => opened.send(LIVE_PING), PING_MS)
			}
			opened.onmessage = (event: MessageEvent<string>) => {
				if (event.data === LIVE_PONG) return
				try {
					const list: unknown = JSON.parse(event.data)
					if (Array.isArray(list)) accept(list as AvatarCharacter[])
				} catch (cause) {
					console.warn('Ignored a malformed character update', cause)
				}
			}
			opened.onclose = () => {
				window.clearInterval(ping)
				if (disposed) return
				dropped = true
				retry = window.setTimeout(connect, RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)])
				attempt += 1
			}
		}

		void refetch()
		connect()
		return () => {
			disposed = true
			window.clearInterval(ping)
			window.clearTimeout(retry)
			socket?.close()
		}
	}, [])

	return { characters, error }
}
